// @flow
import 'es6-promise';
import 'isomorphic-fetch';
import he from 'he';

import { type Option } from 'fp-ts/lib/Option';
import { option } from 'fp-ts';
import { get } from './objects';
import { equals, optionFind, prop } from './functions';
import {
  type $AttributeUnionType,
  Attribute,
  getAttributeSelectedSwatchOption,
  getAttributeCode,
} from '../types/attributes';

const getVimeoThumbnails = videoId => fetch(`https://vimeo.com/api/v2/video/${videoId}.json`)
  .then(resp => resp.json());

export const isVideo = media => get('videoUrl')(media) !== null;


const getVimeoId = (vimeoUrl) => {
  // eslint-disable-next-line
  const match = vimeoUrl.match(/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i);
  return match[1];
};

const mergeThumbsWithPhoto = thumbs => videoId => photo => ({
  ...photo,
  thumb: thumbs[0].thumbnail_large,
  videoId,
});

const maybeGetVideoThumb = media => new Promise((resolve, reject) => {
  if (!isVideo(media)) {
    resolve(media);
  }
  const videoId = getVimeoId(media.videoUrl);
  return getVimeoThumbnails(videoId)
    .then(thumbs => resolve(mergeThumbsWithPhoto(thumbs)(videoId)(media)))
    .catch(reject);
});

export function getVideoThumbs(media: Array<any>) {
  return Promise.all(media.map(photo => maybeGetVideoThumb(photo))).catch(() => media.filter(x => !isVideo(x)));
}

export const getLivePreviewUrl = (
  sku: string,
  attributes: Array<$AttributeUnionType>,
  livePreviewAttributes: Array<string>,
  resolution: ?string,
): Option<string> => {
  const attrs = attributes.map(attribute => Attribute(attribute));

  const orderedAttrValues: Array<string> = livePreviewAttributes
    // Try to get all of the specified attributes
    .map(attrCode => (
      optionFind(attr => getAttributeCode(attr).map(equals(attrCode)).getOrElseValue(false), attrs)
    ))

    // Only include attributes that exist
    .filter(attrOption => attrOption.isSome())

    // Get the selected swatch option's label
    .map(attrOption => attrOption.chain(getAttributeSelectedSwatchOption).chain(prop('label')).getOrElseValue(''))

    // Only include non-blank values
    .filter(value => value.length > 0)

    // Decode any HTML entities
    .map(he.decode);

  if (livePreviewAttributes.length === 0 || (orderedAttrValues.length !== livePreviewAttributes.length)) {
    return option.none;
  }

  const attributesString = orderedAttrValues.join('-');

  const resolutionSuffix = option.fromNullable(resolution).map(r => `_${r}`).getOrElseValue('');

  return option.some(`${sku}-${attributesString}${resolutionSuffix}.jpg`);
};



// WEBPACK FOOTER //
// ./src/helpers/media.js